
"use client";
import type { SiteData, ActionButton, ReportAbuseFormData } from '@/lib/types';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script'; // Importando o componente de Script
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReportAbuseFormDataSchema } from '@/lib/types';
import { submitReportAbuse } from '@/lib/actions/report-abuse-actions';
import { toast } from '@/hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"
import Autoplay from "embla-carousel-autoplay"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Globe,
  Eclipse,
  ExternalLink,
  ChevronRight,
  MessageSquare, // Mantido para TikTok
  MessageCircle, // Adicionado para WhatsApp no header
  Send,
  Share2,
  Eye,
  EyeOff,
  Award,
  Zap,
  ThumbsUp,
  ShieldCheck,
  Lightbulb,
  Users,
  Clock,
  TrendingUp,
  Star,
  Heart,
  CircleUserRound,
  type LucideIcon,
  Image as ImageIconLucide,
  Menu as MenuIcon,
  Smartphone,
  LocateFixed,
  Clock10,
  X as XIcon,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { MiniSiteContactForm } from './ContactForm';
import { cn, isValidImageUrl } from "@/lib/utils";

// Scroll Reveal Wrapper Component
interface ScrollRevealWrapperProps {
  children: ReactNode;
  className?: string;
  delay?: 'delay-0' | 'delay-75' | 'delay-100' | 'delay-150' | 'delay-200' | 'delay-300' | 'delay-500' | 'delay-700' | 'delay-1000';
  initialTranslateY?: 'translate-y-5' | 'translate-y-10' | 'translate-y-20';
  duration?: 'duration-300' | 'duration-500' | 'duration-700' | 'duration-1000';
  threshold?: number;
}

const ScrollRevealWrapper: React.FC<ScrollRevealWrapperProps> = ({
  children,
  className,
  delay = 'delay-100',
  initialTranslateY = 'translate-y-10',
  duration = 'duration-700',
  threshold = 0.1,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = sectionRef.current; 
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    observer.observe(currentRef);
    return () => {
      if (currentRef) { 
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return (
    <div
      ref={sectionRef}
      className={cn(
        'transition-all ease-out',
        duration,
        delay,
        isVisible ? 'opacity-100 translate-y-0' : `opacity-0 ${initialTranslateY}`,
        className
      )}
    >
      {children}
    </div>
  );
};


// Helper para renderizar ícones dinamicamente
const IconRenderer: React.FC<{ iconName?: string; className?: string }> = ({ iconName, className }) => {
  if (!iconName) return <Eclipse className={className} />;

  const iconMap: Record<string, LucideIcon> = {
    Award, Zap, ThumbsUp, ShieldCheck, Lightbulb, Users, Clock, TrendingUp, Star, Heart,
    MapPin, Phone, Mail, Instagram, Facebook, Linkedin, Twitter, Youtube, Globe, Eclipse,
    ExternalLink, ChevronRight, MessageSquare, Send, Share2, Eye, EyeOff, CircleUserRound, Smartphone, LocateFixed, Clock10, AlertTriangle
  };
  const IconComponent = iconMap[iconName] || Star;
  return <IconComponent className={className} />;
};

// Helper para renderizar HTML de forma segura
const SafeHtmlRenderer: React.FC<{ htmlString?: string; className?: string }> = ({ htmlString, className }) => {
  if (!htmlString) return null;
  return <div className={className} dangerouslySetInnerHTML={{ __html: htmlString }} />;
};

// Helper para renderizar botões de ação com cores personalizadas
const RenderActionButton: React.FC<{
  button?: ActionButton;
  siteData: SiteData;
  defaultText?: string;
  defaultLink?: string;
  additionalClasses?: string;
  children?: React.ReactNode;
  forcedBgColor?: string;
  forcedTextColor?: string;
}> = ({ button, siteData, defaultText = 'Saiba Mais', defaultLink = '#', additionalClasses = '', children, forcedBgColor, forcedTextColor }) => {
  if (!button?.text && !children) return null;

  const text = button?.text || children || defaultText;
  const link = button?.link || defaultLink;

  // Usa a cor de destaque como padrão, a menos que uma cor específica seja fornecida no botão
  const bgColor = forcedBgColor || button?.backgroundColor || 'var(--template-accent-color)';
  const textColor = forcedTextColor || button?.textColor || '#FFFFFF'; // TODO: Calcular contraste para decidir entre branco/preto

  return (
    <Link
      href={link}
      className={`inline-flex items-center rounded-md px-6 py-3 text-lg font-semibold shadow-md transition-colors hover:opacity-90 ${additionalClasses}`}
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      {text}
      {button?.text && <ChevronRight className="ml-2 h-5 w-5" />}
    </Link>
  );
};

// Função utilitária para gerar links de navegação
const generateNavigationLinks = (data: SiteData): { label: string; href: string }[] => {
    const links: { label: string; href: string }[] = [];
    if (typeof document === 'undefined') return links; // Guard clause for server-side or early client-side rendering

    const localTempDiv = document.createElement('div');

    const getCleanLabel = (htmlString: string | undefined, fallbackLabel: string): string => {
      if (!htmlString || htmlString.trim() === "" || htmlString.toLowerCase() === 'string') return fallbackLabel;
      localTempDiv.innerHTML = htmlString;
      return (localTempDiv.textContent || localTempDiv.innerText || fallbackLabel).trim();
    };

    if (data.block2_isActive ?? true) {
      links.push({ label: getCleanLabel(data.companyName || data.siteName, 'Início'), href: '#hero' });
    }
    if (data.block3_isActive ?? true) {
      links.push({ label: getCleanLabel(data.block3_title, 'Sobre Nós'), href: '#sobre' });
    }
    if (data.block4_isActive ?? true) {
      links.push({ label: getCleanLabel(data.block4_title, 'Nossos Serviços'), href: '#servicos' });
    }
    if ((data.block_diff_isActive ?? true) && data.block_diff_items && data.block_diff_items.length > 0) {
      links.push({ label: getCleanLabel(data.block_diff_title, 'Nossos Diferenciais'), href: '#diferenciais' });
    }
    if ((data.block5_isActive ?? true) && data.block5_testimonials && data.block5_testimonials.length > 0) {
      links.push({ label: getCleanLabel(data.block5_title, 'O Que Dizem Nossos Clientes'), href: '#depoimentos' });
    }
    if ((data.block6_isActive ?? true) && data.block6_clientLogos && data.block6_clientLogos.length > 0) {
      links.push({ label: getCleanLabel(data.block6_title, 'Nossos Clientes'), href: '#clientes' });
    }
    if ((data.block7_isActive ?? true) && data.block7_galleryImages && data.block7_galleryImages.length > 0) {
      links.push({ label: getCleanLabel(data.block7_title, 'Galeria'), href: '#galeria' });
    }
    if ((data.block8_isActive ?? true) && data.block8_faqs && data.block8_faqs.length > 0) {
      links.push({ label: getCleanLabel(data.block8_title, 'Perguntas Frequentes'), href: '#faq' });
    }
     if (data.block10_isActive ?? true) {
      links.push({ label: getCleanLabel(data.block10_title, 'Localização'), href: '#local' });
    }
    if (data.block9_isActive ?? true) {
      links.push({ label: 'Fale Conosco', href: '#cta' });
    }
    if (data.block_footer_isActive ?? true) {
      links.push({ label: 'Contato', href: '#contato'});
    }
    return links;
  };


// --- Hero Section Component (Block 2) ---
const HeroSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block2_isActive ?? true;
  if (!isActive) return null;

  const titleHtml = (data.block2_title && data.block2_title.toLowerCase() !== 'string') ? data.block2_title : 'Bem-vindo!';
  const subtitleHtml = (data.block2_subtitle && data.block2_subtitle.toLowerCase() !== 'string') ? data.block2_subtitle : 'Conheça nossos produtos e serviços.';

  const heroImageSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(data.block2_backgroundImageUrl) ? data.block2_backgroundImageUrl : 'https://placehold.co/1920x1080.png';
  const isPexelsImage = typeof heroImageSrc === 'string' && heroImageSrc.includes('images.pexels.com');
  
  return (
    <ScrollRevealWrapper delay="delay-0" duration="duration-1000" threshold={0.05}>
      <section
        id="hero"
        className="relative flex min-h-[70vh] items-center justify-center overflow-hidden py-20 text-center text-white md:min-h-screen"
      >
        <Image
            src={heroImageSrc!}
            alt={data.siteName || 'Imagem de fundo do herói'}
            fill
            priority
            className="object-cover"
            data-ai-hint={data.block2_backgroundImage_ai_hint || "hero background"}
            sizes="100vw"
            unoptimized={isPexelsImage}
          />
        {data.block2_overlayColor && (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: data.block2_overlayColor }}
          />
        )}
        <div className="container relative z-10 mx-auto px-4">
          <SafeHtmlRenderer htmlString={titleHtml} className="prose prose-invert mx-auto font-headline text-4xl font-bold md:text-5xl lg:text-6xl [&_h1]:text-inherit [&_h1]:font-inherit [&_h2]:text-inherit [&_h2]:font-inherit" />
          {subtitleHtml && (
            <div className="prose prose-xl mx-auto mt-6 max-w-3xl text-white/90">
              <SafeHtmlRenderer htmlString={subtitleHtml} />
            </div>
          )}
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            {data.block2_buttons?.map((button, index) => {
              if (!button.text) return null;

              const bgColor = button.backgroundColor || (index === 0 ? 'var(--template-accent-color)' : 'transparent');
              const textColor = button.textColor || (index === 0 ? '#000000' : '#FFFFFF');
              const borderClass = index === 1 ? 'border-2 border-white' : '';
              const hoverClasses = index === 0
                ? 'hover:opacity-90'
                : 'hover:bg-white hover:text-[var(--template-primary-color)]';

              return (
                <Link
                  key={index}
                  href={button.link || '#'}
                  className={`rounded-md px-8 py-3 text-lg font-semibold transition-colors ${borderClass} ${hoverClasses}`}
                  style={{ backgroundColor: bgColor, color: textColor }}
                >
                  {button.text}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- About Us Section Component (Block 3) ---
const AboutUsSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block3_isActive ?? true;
  if (!isActive) {
    return null;
  }

  const textHtml = (data.block3_text && data.block3_text.toLowerCase() !== 'string') ? data.block3_text : '<p>Detalhes sobre a empresa aqui.</p>';
  const photoAlignment = 'right';
  const aboutImageSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(data.block3_photoUrl) ? data.block3_photoUrl : 'https://placehold.co/800x600.png';
  const isPexelsImage = typeof aboutImageSrc === 'string' && aboutImageSrc.includes('images.pexels.com');

  return (
    <ScrollRevealWrapper>
      <section id="sobre" className="py-16 md:py-24" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto px-4">
          <div className="grid items-center gap-8 md:grid-cols-2 lg:gap-12">
            <div className={`${photoAlignment === 'right' ? 'md:order-1' : 'md:order-2'}`}>
              <h2 className="font-headline text-3xl font-bold text-gray-800 md:text-4xl">
                {(data.block3_title && data.block3_title.toLowerCase() !== 'string') ? data.block3_title : 'Sobre Nossa Empresa'}
              </h2>
              <SafeHtmlRenderer htmlString={textHtml} className="prose prose-lg mt-6 text-gray-600" />
              {data.block3_actionButton?.text && (
                <div className="mt-8">
                  <RenderActionButton button={data.block3_actionButton} siteData={data} />
                </div>
              )}
            </div>
            <div className={`relative aspect-video overflow-hidden rounded-lg shadow-xl ${photoAlignment === 'right' ? 'md:order-2' : 'md:order-1'}`}>
                <Image
                  src={aboutImageSrc!}
                  alt={data.block3_title || 'Foto sobre a empresa'}
                  fill
                  className="object-cover"
                  data-ai-hint={data.block3_photo_ai_hint || "company team office"}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  unoptimized={isPexelsImage}
                />
            </div>
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Services Section Component (Block 4) ---
const ServicesSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block4_isActive ?? true;
  if (!isActive) {
    return null;
  }

  const textHtml = (data.block4_text && data.block4_text.toLowerCase() !== 'string') ? data.block4_text : '<p>Informações sobre nossos serviços.</p>';
  const photoAlignment = 'left';
  const servicesImageSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(data.block4_photoUrl) ? data.block4_photoUrl : 'https://placehold.co/800x600.png';
  const isPexelsImage = typeof servicesImageSrc === 'string' && servicesImageSrc.includes('images.pexels.com');

  const sectionStyle = {
    backgroundColor: data.secondaryColor || 'var(--template-secondary-color)',
  };

  return (
    <ScrollRevealWrapper>
      <section id="servicos" className="py-16 md:py-24" style={sectionStyle}>
        <div className="container mx-auto px-4">
          <div className="grid items-center gap-8 md:grid-cols-2 lg:gap-12">
            <div className={`${photoAlignment === 'left' ? 'md:order-2' : 'md:order-1'}`}>
              <h2 className="font-headline text-3xl font-bold md:text-4xl text-white">
                {(data.block4_title && data.block4_title.toLowerCase() !== 'string') ? data.block4_title : 'Nossos Serviços'}
              </h2>
              <SafeHtmlRenderer htmlString={textHtml} className="prose prose-lg prose-invert mt-6" />
              {data.block4_actionButton?.text && (
                <div className="mt-8">
                  <RenderActionButton
                    button={data.block4_actionButton}
                    siteData={data}
                  />
                </div>
              )}
            </div>
            <div className={`relative aspect-video overflow-hidden rounded-lg shadow-xl ${photoAlignment === 'left' ? 'md:order-1' : 'md:order-2'}`}>
                <Image
                  src={servicesImageSrc!}
                  alt={data.block4_title || 'Foto dos serviços'}
                  fill
                  className="object-cover"
                  data-ai-hint={data.block4_photo_ai_hint || "product service item"}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  unoptimized={isPexelsImage}
                />
            </div>
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Differentiators Section Component (Block Diff) ---
const DifferentiatorsSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block_diff_isActive ?? true;
  const items = data.block_diff_items || [];

  if (!isActive || items.length === 0) {
    return null;
  }

  const getItemsPerRowConfig = (count: number): number[] => {
    if (count <= 0) return [];
    if (count <= 4) return [count];
    if (count === 5) return [3, 2];
    if (count === 6) return [3, 3];
    if (count === 7) return [4, 3];
    if (count === 8) return [4, 4];
    if (count === 9) return [4, 4, 1];
    if (count === 10) return [4, 4, 2];

    const defaultConfig: number[] = [];
    let tempCount = count;
    while (tempCount > 0) {
      const itemsThisRow = Math.min(tempCount, 4);
      defaultConfig.push(itemsThisRow);
      tempCount -= itemsThisRow;
    }
    return defaultConfig;
  };

  const rowCounts = getItemsPerRowConfig(items.length);
  let currentItemIndex = 0;

  return (
    <ScrollRevealWrapper>
      <section id="diferenciais" className="py-16 md:py-24 bg-slate-100">
        <div className="container mx-auto px-4">
          <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-4">
            {data.block_diff_title || 'Nossos Diferenciais'}
          </h2>
          {data.block_diff_subtitle && (
              <p className="text-xl text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                  {data.block_diff_subtitle}
              </p>
          )}

          <div className="space-y-4 md:space-y-6 lg:space-y-8">
            {rowCounts.map((countInRow, rowIndex) => {
              const rowItems = items.slice(currentItemIndex, currentItemIndex + countInRow);
              currentItemIndex += countInRow;
              if (rowItems.length === 0) return null;
              return (
                <div key={`diff-row-${rowIndex}`} className="flex flex-wrap justify-center gap-4 md:gap-6 lg:gap-8">
                  {rowItems.map(item => (
                    <div
                      key={item.id}
                      className="flex flex-col items-center rounded-lg bg-white p-4 text-center shadow-lg transition-shadow hover:shadow-xl w-full sm:w-[calc(50%-0.5rem)] md:w-[calc(33.33%-1rem)] lg:w-[calc(25%-1.125rem)] max-w-[280px] flex-shrink-0"
                    >
                      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-template-primary text-white">
                        <IconRenderer iconName={item.icon} className="h-7 w-7" />
                      </div>
                      <h3 className="font-headline text-lg font-semibold text-gray-800 mb-1.5">{item.title}</h3>
                      <p className="text-sm text-gray-600 flex-grow min-h-[4.5rem]">{item.description}</p>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {data.block_diff_actionButton?.text && (
              <div className="mt-12 text-center">
                <RenderActionButton button={data.block_diff_actionButton} siteData={data} />
              </div>
          )}
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Testimonials Section Component (Block 5) ---
const TestimonialsSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block5_isActive ?? true;
  const testimonials = data.block5_testimonials || [];

  if (!isActive || testimonials.length === 0) {
    return null;
  }
  
  return (
    <ScrollRevealWrapper>
      <section id="depoimentos" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-4">
            {data.block5_title || 'O Que Nossos Clientes Dizem'}
          </h2>
          {data.block5_subtitle && (
              <p className="text-xl text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                  {data.block5_subtitle}
              </p>
          )}

          <Carousel
              opts={{ align: "start", loop: testimonials.length > 3 }}
              className="w-full"
          >
              <CarouselContent className="-ml-4">
                  {testimonials.map((testimonial) => {
                  const testimonialImageSrc = isValidImageUrl(testimonial.photoUrl) ? testimonial.photoUrl : null;
                  const isPexelsImage = typeof testimonialImageSrc === 'string' && testimonialImageSrc.includes('images.pexels.com');
                  return (
                      <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                          <div className="p-1">
                              <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-slate-50/50 p-6 shadow-sm">
                                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 border-template-primary bg-slate-200">
                                      {testimonialImageSrc ? (
                                        <Image
                                            src={testimonialImageSrc}
                                            alt={`Foto de ${testimonial.name}`}
                                            width={100}
                                            height={100}
                                            className="h-full w-full object-cover"
                                            data-ai-hint={`person portrait ${testimonial.name.split(' ')[0]}`}
                                            unoptimized={isPexelsImage}
                                        />
                                      ) : (
                                        <CircleUserRound className="h-12 w-12 text-slate-400" />
                                      )}
                                  </div>
                                  <p className="flex-grow text-center text-gray-600 italic">&quot;{testimonial.text}&quot;</p>
                                  <div className="mt-4 text-center">
                                      <p className="font-headline text-lg font-semibold text-gray-800">{testimonial.name}</p>
                                      <p className="text-sm text-gray-500">{testimonial.title}</p>
                                  </div>
                              </div>
                          </div>
                      </CarouselItem>
                  );
                  })}
              </CarouselContent>
              <CarouselPrevious className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 text-template-primary hover:text-template-primary/80 bg-white/80 hover:bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed" />
              <CarouselNext className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 text-template-primary hover:text-template-primary/80 bg-white/80 hover:bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed" />
          </Carousel>
          
          {data.block5_actionButton?.text && (
            <div className="mt-12 text-center">
              <RenderActionButton button={data.block5_actionButton} siteData={data} />
            </div>
          )}
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Client Logos Section Component (Block 6) ---
const ClientLogosSection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block6_isActive ?? true;
    const logos = data.block6_clientLogos || [];

    const [api, setApi] = React.useState<CarouselApi>()
    const [current, setCurrent] = React.useState(0)
    const [count, setCount] = React.useState(0)

    const plugin = React.useRef(
        Autoplay({ delay: 2500, stopOnInteraction: true, stopOnMouseEnter: true })
    );

    React.useEffect(() => {
        if (!api) return;
        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap() + 1);
        api.on("select", () => setCurrent(api.selectedScrollSnap() + 1));
        api.on("reInit", () => {
            setCount(api.scrollSnapList().length);
            setCurrent(api.selectedScrollSnap() + 1);
        });
      }, [api]);

    if (!isActive || logos.length === 0) {
      return null;
    }

    return (
      <ScrollRevealWrapper>
        <section id="clientes" className="py-12 md:py-20 bg-slate-100">
            <div className="container mx-auto px-4">
                <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-10">
                    {data.block6_title || 'Clientes que Confiam em Nós'}
                </h2>
                <div className="relative">
                    <Carousel setApi={setApi} plugins={[plugin.current]} opts={{ align: "start", loop: logos.length > 5 }} className="w-full">
                        <CarouselContent className="-ml-2 md:-ml-4">
                            {logos.map((logo) => {
                                const placeholderText = encodeURIComponent(logo.altText || "Cliente").replace(/%20/g, '+');
                                const clientLogoSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(logo.logoUrl) 
                                    ? logo.logoUrl 
                                    : `https://placehold.co/150x150.png?text=${placeholderText}`;
                                
                                const isPexelsImage = typeof clientLogoSrc === 'string' && clientLogoSrc.includes('images.pexels.com');
                                return (
                                    <CarouselItem key={logo.id} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 flex items-center justify-center">
                                        <div className="flex-shrink-0 w-28 h-28 aspect-square relative flex items-center justify-center filter grayscale transition hover:grayscale-0">
                                            <Image src={clientLogoSrc!} alt={logo.altText || `Logo cliente`} fill sizes="(max-width: 768px) 20vw, 10vw" className="object-contain p-1" data-ai-hint="client brand" unoptimized={isPexelsImage} />
                                        </div>
                                    </CarouselItem>
                                )
                            })}
                        </CarouselContent>
                        <CarouselPrevious className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 text-template-primary hover:text-template-primary/80 bg-white/80 hover:bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed" />
                        <CarouselNext className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 text-template-primary hover:text-template-primary/80 bg-white/80 hover:bg-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed" />
                    </Carousel>
                    {count > 0 && ( 
                          <div className="flex justify-center space-x-2 mt-6">
                            {Array.from({ length: count }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => api?.scrollTo(index)}
                                className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ease-in-out ${
                                current === index + 1 ? 'bg-template-primary scale-125' : 'bg-gray-400 hover:bg-gray-500 scale-100'
                                }`}
                                aria-label={`Ir para slide ${index + 1}`}
                            />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
      </ScrollRevealWrapper>
    );
};

// --- Gallery Section Component (Block 7) ---
const GallerySection: React.FC<{ data: SiteData }> = ({ data }) => {
    const isActive = data.block7_isActive ?? true;
    const images = data.block7_galleryImages || [];

    if (!isActive || images.length === 0) {
      return null;
    }

    return (
      <ScrollRevealWrapper>
        <section id="galeria" className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4">
                <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-4">
                    {data.block7_title || 'Nosso Trabalho'}
                </h2>
                {data.block7_subtitle && (
                      <p className="text-xl text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                        {data.block7_subtitle}
                      </p>
                )}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {images.map((imageItem) => {
                        const galleryImageSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(imageItem.imageUrl) ? imageItem.imageUrl : 'https://placehold.co/600x400.png';
                        const isPexelsImage = typeof galleryImageSrc === 'string' && galleryImageSrc.includes('images.pexels.com');
                        const hint = imageItem.imageHint || imageItem.altText?.trim().split(/\s+/).slice(0, 2).join(' ') || 'portfolio work';

                        return (
                            <div key={imageItem.id} className="group relative aspect-video overflow-hidden rounded-lg shadow-lg">
                                <Image
                                    src={galleryImageSrc!}
                                    alt={imageItem.altText || `Imagem da galeria ${imageItem.id}`}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    data-ai-hint={hint}
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                    unoptimized={isPexelsImage}
                                />
                                {imageItem.altText && (
                                    <div className="absolute inset-x-0 bottom-0 bg-black/50 p-2 text-center text-xs text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                        {imageItem.altText}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {data.block7_actionButton?.text && (
                  <div className="mt-12 text-center">
                    <RenderActionButton button={data.block7_actionButton} siteData={data} />
                  </div>
                )}
            </div>
        </section>
      </ScrollRevealWrapper>
    );
};

// --- FAQ Section Component (Block 8) ---
const FAQSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block8_isActive ?? true;
  const faqs = data.block8_faqs || [];

  if (!isActive || faqs.length === 0) {
    return null;
  }

  return (
    <ScrollRevealWrapper>
      <section id="faq" className="py-16 md:py-24 bg-slate-100">
        <div className="container mx-auto max-w-3xl px-4">
          <h2 className="font-headline text-3xl font-bold text-center text-gray-800 md:text-4xl mb-4">
            {data.block8_title || 'Perguntas Frequentes'}
          </h2>
          {data.block8_subtitle && (
            <p className="text-xl text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              {data.block8_subtitle}
            </p>
          )}
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((faqItem) => (
              <AccordionItem value={faqItem.id} key={faqItem.id} className="rounded-lg border bg-white shadow-sm data-[state=open]:shadow-md">
                <AccordionTrigger className="px-6 py-4 text-left font-headline text-lg font-semibold text-gray-800 hover:no-underline">
                  {faqItem.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 pt-0">
                  <SafeHtmlRenderer htmlString={faqItem.answer} className="prose prose-sm" />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Location & Contact Section Component (Block 10) ---
const LocationContactSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block10_isActive ?? true;
  if (!isActive) return null;

  const mapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  
  const addressFromFields = [
      data.address_street,
      data.address_number,
      data.address_neighborhood,
      data.address_city,
      data.address_state,
  ].filter(Boolean).join(', ');

  const addressString = (addressFromFields || data.address || '').replace(/CEP\s*\d{5}-?\d{3}/i, '').trim();
  const mapQuery = addressString || data.companyName || '';

  const mapSrc = `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(mapQuery)}`;

  const showColumn1 = (data.block10_column1_isActive ?? true) && data.block10_column1_text && data.block10_column1_text.trim() !== '';
  const showColumn2 = (data.block10_column2_map_isActive ?? true) && mapQuery && mapsApiKey;
  const showColumn3 = (data.block10_column3_form_isActive ?? true) && data.contactEmail;

  let visibleColumnsCount = 0;
  if (showColumn1) visibleColumnsCount++;
  if (showColumn2) visibleColumnsCount++;
  if (showColumn3) visibleColumnsCount++;
  
  const gridColsClass = cn(
    "md:grid-cols-1", 
    visibleColumnsCount === 2 && "md:grid-cols-2",
    visibleColumnsCount >= 3 && "md:grid-cols-3"
  );

  return (
    <ScrollRevealWrapper>
      <section
          id="local"
          className="py-16 md:py-24"
          style={{ backgroundColor: data.secondaryColor || 'var(--template-secondary-color)'}}
      >
        <div className="container mx-auto px-4">
          <h2 className="font-headline text-3xl font-bold text-center text-white md:text-4xl mb-12">
            {(data.block10_title && data.block10_title.toLowerCase() !== 'string') ? data.block10_title : 'Nossa Localização e Contato'}
          </h2>
          <div className={cn("grid grid-cols-1 gap-8 md:items-start", gridColsClass)}>
            {showColumn1 && (
              <div className="md:col-span-1 flex flex-col justify-center h-full">
                <SafeHtmlRenderer htmlString={data.block10_column1_text} className="prose prose-base prose-invert" />
              </div>
            )}
            {showColumn2 && (
              <div className="md:col-span-1 h-[450px]">
                <iframe
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={mapSrc}
                  className="rounded-md shadow-md"
                  title={`Mapa para ${data.companyName || 'nossa localização'}`}
                ></iframe>
              </div>
            )}
            {showColumn3 && (
              <div className="md:col-span-1">
                <MiniSiteContactForm
                  contactToEmail={data.contactEmail!}
                  title={(data.block10_column3_formTitle && data.block10_column3_formTitle.toLowerCase() !== 'string') ? data.block10_column3_formTitle : "Fale Conosco"}
                  titleClassName="text-white"
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- CTA Section Component (Block 9) ---
const CTASection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block9_isActive ?? true;
  if (!isActive) return null;

  const ctaImageSrc = typeof isValidImageUrl === 'function' && isValidImageUrl(data.block9_backgroundImageUrl) ? data.block9_backgroundImageUrl : 'https://placehold.co/1920x720.png';
  const isPexelsImage = typeof ctaImageSrc === 'string' && ctaImageSrc.includes('images.pexels.com');

  return (
    <ScrollRevealWrapper>
      <section
        id="cta"
        className="relative overflow-hidden bg-gray-800 py-20 text-white md:py-32"
      >
        <Image
            src={ctaImageSrc!}
            alt={data.block9_impactPhrase || 'Imagem de fundo do CTA'}
            fill
            className="object-cover"
            data-ai-hint={data.block9_backgroundImage_ai_hint || "call to action"}
            sizes="100vw"
            unoptimized={isPexelsImage}
        />
        {data.block9_overlayColor && (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: data.block9_overlayColor }}
          />
        )}
        <div className="container relative z-10 mx-auto px-4 text-center">
          <h2 className="font-headline text-3xl font-bold md:text-4xl lg:text-5xl">
            {(data.block9_impactPhrase && data.block9_impactPhrase.toLowerCase() !== 'string') ? data.block9_impactPhrase : 'Pronto para Começar?'}
          </h2>
          {data.block9_actionButton?.text && (
            <div className="mt-8">
              <RenderActionButton
                  button={data.block9_actionButton}
                  siteData={data}
                  additionalClasses="px-10 py-4 text-lg"
              />
            </div>
          )}
        </div>
      </section>
    </ScrollRevealWrapper>
  );
};

// --- Report Abuse Modal and Form Component ---
const ReportAbuseModal: React.FC<{ siteData: SiteData }> = ({ siteData: modalSiteDataProp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ReportAbuseFormData>({
    resolver: zodResolver(ReportAbuseFormDataSchema),
    defaultValues: {
      reportedSiteSubdomain: modalSiteDataProp.subdomain,
      reportedSiteCustomDomain: modalSiteDataProp.customDomain || '',
    }
  });

  const onReportSubmit: SubmitHandler<ReportAbuseFormData> = async (formData) => {
    setIsSubmittingReport(true);
    try {
      const result = await submitReportAbuse(formData);
      if (result.success) {
        toast({ title: "Denúncia Enviada", description: result.message });
        reset();
        setIsOpen(false);
      } else {
        toast({ title: "Erro ao Enviar Denúncia", description: result.message, variant: "destructive" });
      }
    } catch (error) {
      console.error("Erro ao submeter denúncia:", error);
      toast({ title: "Erro Inesperado", description: "Ocorreu um problema ao enviar sua denúncia.", variant: "destructive" });
    } finally {
      setIsSubmittingReport(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      reset({
        reportedSiteSubdomain: modalSiteDataProp.subdomain,
        reportedSiteCustomDomain: modalSiteDataProp.customDomain || '',
        reporterName: '',
        reporterEmail: '',
        reporterPhone: '',
        reportMessage: ''
      });
    }
  }, [isOpen, reset, modalSiteDataProp.subdomain, modalSiteDataProp.customDomain]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <button className="mt-4 text-sm font-semibold text-slate-500 hover:text-template-primary hover:underline flex items-center gap-1">
          <AlertTriangle className="h-4 w-4" /> Denunciar conteúdo ilegal ou abusivo
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-headline">Denunciar Conteúdo</DialogTitle>
          <DialogDescription>
            Se você acredita que o conteúdo deste site ({modalSiteDataProp.customDomain || `${modalSiteDataProp.subdomain}.website.app.br`}) é ilegal, abusivo ou viola nossos termos, por favor, preencha o formulário abaixo.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onReportSubmit)} className="space-y-4">
          <div className="absolute opacity-0 h-0 w-0">
            <label htmlFor="report-honeypot">Não preencha este campo</label>
            <input id="report-honeypot" tabIndex={-1} autoComplete="off" {...register("honeypot")} />
          </div>
          <div>
            <Label htmlFor="reporterName">Seu Nome</Label>
            <Input id="reporterName" {...register("reporterName")} disabled={isSubmittingReport} />
            {errors.reporterName && <p className="text-xs text-destructive mt-1">{errors.reporterName.message}</p>}
          </div>
          <div>
            <Label htmlFor="reporterEmail">Seu E-mail</Label>
            <Input id="reporterEmail" type="email" {...register("reporterEmail")} disabled={isSubmittingReport} />
            {errors.reporterEmail && <p className="text-xs text-destructive mt-1">{errors.reporterEmail.message}</p>}
          </div>
          <div>
            <Label htmlFor="reporterPhone">Seu Telefone (Opcional)</Label>
            <Input id="reporterPhone" type="tel" {...register("reporterPhone")} disabled={isSubmittingReport} />
          </div>
          <div>
            <Label htmlFor="reportMessage">Mensagem da Denúncia</Label>
            <Textarea id="reportMessage" rows={5} {...register("reportMessage")} disabled={isSubmittingReport} />
            {errors.reportMessage && <p className="text-xs text-destructive mt-1">{errors.reportMessage.message}</p>}
          </div>
          <input type="hidden" {...register("reportedSiteSubdomain")} />
          <input type="hidden" {...register("reportedSiteCustomDomain")} />
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isSubmittingReport}>
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmittingReport} style={{ backgroundColor: 'var(--template-primary-color)'}}>
              {isSubmittingReport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Enviar Denúncia
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Footer Section Component ---
const FooterSection: React.FC<{ data: SiteData }> = ({ data }) => {
  const isActive = data.block_footer_isActive ?? true;
  if (!isActive) return null;
  
  const [sitemapLinks, setSitemapLinks] = useState<{ label: string; href: string }[]>([]);

  useEffect(() => {
    setSitemapLinks(generateNavigationLinks(data));
  }, [data]);


  const socialIconMap: Record<string, LucideIcon> = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    x: Twitter,
    youtube: Youtube,
    tiktok: MessageSquare,
    other: Globe,
  };

  const buildSocialLink = (link: SiteData['socialLinks'][0]) => {
    if (!link) return '#';
    if (link.type === 'other') return link.urlSuffix || '#';
    const prefixes: Record<string, string> = {
        instagram: 'https://instagram.com/',
        facebook: 'https://facebook.com/',
        linkedin: 'https://linkedin.com/company/',
        x: 'https://x.com/',
        youtube: 'https://youtube.com/',
        tiktok: 'https://tiktok.com/@',
    };
    return `${prefixes[link.type] || ''}${link.urlSuffix || ''}`;
  };

  const rawFooterLogoUrl = data.block_footer_logoUrl || data.companyLogoUrl;
  let displayFooterLogoAsImage = false;
  let footerLogoSrcForImageComponent = typeof isValidImageUrl === 'function' && isValidImageUrl(rawFooterLogoUrl) ? rawFooterLogoUrl! : 'https://placehold.co/150x60.png?text=Logo'; // Fallback for logo

  if (typeof isValidImageUrl === 'function' && isValidImageUrl(rawFooterLogoUrl)) {
      displayFooterLogoAsImage = true;
  }
  const isPexelsImageForLogo = footerLogoSrcForImageComponent.includes('images.pexels.com');

  const currentSiteUrl = data.customDomain ? `https://${data.customDomain}` : `https://${data.subdomain}.${process.env.NEXT_PUBLIC_ROOT_DOMAIN || 'website.app.br'}`;

  // Determine visibility of footer columns
  const hasAddressDetails = data.address || data.address_street || data.address_city || data.address_zipCode;
  const showLogoInfoCol = true; 
  const showContactDetailsCol = (data.block_footer_showAddress && hasAddressDetails) || (data.block_footer_showPhone && data.phone) || (data.block_footer_showWhatsapp && data.whatsapp) || (data.block_footer_showEmail && data.contactEmail);
  const showSitemapCol = (data.block_footer_showSitemap ?? true) && sitemapLinks.length > 0;
  const showContactFormCol = (data.block_footer_showContactForm ?? true) && data.contactEmail;

  let visibleFooterColumns = 0;
  if (showLogoInfoCol) visibleFooterColumns++;
  if (showContactDetailsCol) visibleFooterColumns++;
  if (showSitemapCol) visibleFooterColumns++;
  if (showContactFormCol) visibleFooterColumns++;
  
  const footerGridLayoutClasses = cn(
    "grid-cols-1 gap-10 md:grid-cols-2", 
    visibleFooterColumns >= 4 && "lg:grid-cols-4",
    visibleFooterColumns === 3 && "lg:grid-cols-3",
    visibleFooterColumns === 2 && "lg:grid-cols-2",
    visibleFooterColumns < 2 && "lg:grid-cols-1"
  );


  return (
    <footer id="contato" className="bg-slate-100 text-slate-700 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className={cn("grid", footerGridLayoutClasses)}>

          {showLogoInfoCol && (
            <div>
              {displayFooterLogoAsImage && footerLogoSrcForImageComponent ? (
                <Link href="#hero" className="mb-4 inline-block">
                  <Image
                    src={footerLogoSrcForImageComponent}
                    alt={`Logo de ${data.siteName || 'Empresa'} no rodapé`}
                    width={180}
                    height={60}
                    className="h-auto max-h-[60px] object-contain"
                    data-ai-hint="company logo footer"
                    unoptimized={isPexelsImageForLogo}
                  />
                </Link>
              ) : (
                  <Link href="#hero" className="mb-4 inline-block font-headline text-xl font-bold text-gray-800">
                      {(data.siteName && data.siteName.toLowerCase() !== 'string') ? data.siteName : (data.companyName && data.companyName.toLowerCase() !== 'string' ? data.companyName : "Meu Site Incrível")}
                  </Link>
              )}
              {(data.block_footer_additionalText && data.block_footer_additionalText.toLowerCase() !== 'string') && (
                <SafeHtmlRenderer htmlString={data.block_footer_additionalText} className="prose-sm text-slate-600" />
              )}
              {data.socialLinks && data.socialLinks.length > 0 && (data.block_footer_showSocialLinks ?? true) && (
                <div className="mt-4 flex space-x-3">
                  {data.socialLinks.map(link => {
                    if (!link || !link.urlSuffix) return null;
                    const SocialIcon = socialIconMap[link.type] || Globe;
                    const fullUrl = buildSocialLink(link);
                    return (
                      <a
                        key={link.id}
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Link para ${data.companyName || data.siteName} no ${link.type}`}
                        className="text-slate-500 hover:text-template-primary transition-colors"
                      >
                        <SocialIcon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {showContactDetailsCol && (
            <div>
              <h4 className="font-headline text-lg font-semibold text-gray-800 mb-4">Contato</h4>
              <ul className="space-y-3 text-sm">
                {(data.block_footer_showAddress ?? true) && hasAddressDetails && (
                  <li className="flex items-start">
                    <MapPin className="mr-3 mt-1 h-4 w-4 shrink-0 text-template-primary" />
                    <span>
  {(data.address_street || data.address_city) ? (
    <React.Fragment>
      {data.address_street}{data.address_number ? `, ${data.address_number}` : ''}
      {data.address_complement ? `, ${data.address_complement}` : ''}
      <br />
      {data.address_neighborhood ? `${data.address_neighborhood}, ` : ''}
      {data.address_city || ''} - {data.address_state || ''}
      <br />
      CEP: {data.address_zipCode || ''}
    </React.Fragment>
  ) : (
    data.address
  )}
</span>
                  </li>
                )}
                {(data.block_footer_showPhone ?? true) && data.phone && (
                  <li className="flex items-center">
                    <Phone className="mr-3 h-4 w-4 shrink-0 text-template-primary" />
                    <a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} className="hover:text-template-primary">
                      {data.phoneDDI || ''} {data.phone}
                    </a>
                  </li>
                )}
                {(data.block_footer_showWhatsapp ?? true) && data.whatsapp && (
                  <li className="flex items-center">
                    <MessageCircle className="mr-3 h-4 w-4 shrink-0 text-template-primary" />
                      <a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-template-primary">
                        {data.whatsappDDI || ''} {data.whatsapp} (WhatsApp)
                    </a>
                  </li>
                )}
                {(data.block_footer_showEmail ?? true) && data.contactEmail && (
                  <li className="flex items-center">
                    <Mail className="mr-3 h-4 w-4 shrink-0 text-template-primary" />
                    <a href={`mailto:${data.contactEmail}`} className="hover:text-template-primary">
                      {data.contactEmail}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}

          {showSitemapCol && (
            <div>
              <h4 className="font-headline text-lg font-semibold text-gray-800 mb-4">Navegue</h4>
              <ul className="space-y-2 text-sm">
                {sitemapLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className="hover:text-template-primary">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}


          {showContactFormCol && (
              <div className={cn("md:col-span-2", visibleFooterColumns === 4 && "lg:col-span-1", visibleFooterColumns === 3 && "lg:col-span-1", visibleFooterColumns < 3 && "md:col-span-2")}>
                <MiniSiteContactForm
                  contactToEmail={data.contactEmail!}
                  title="Fale Conosco"
                  titleClassName="text-gray-800"
                />
              </div>
          )}
        </div>

        <div className="mt-10 border-t border-slate-300 pt-8 text-center text-sm text-slate-600">
          <p className="mb-2">&copy; {new Date().getFullYear()} {data.siteName || "Seu Site"}. Todos os direitos reservados.</p>
          <div className="mt-1">
            {data.privacyPolicyContent && (
              <Dialog>
                <DialogTrigger asChild>
                  <button className="text-sm text-slate-700 hover:text-template-primary hover:underline cursor-pointer">
                    Política de Privacidade
                  </button>
                </DialogTrigger>
                 <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col p-0">
                    <DialogHeader className="p-6 pb-4 border-b">
                        <DialogTitle className="text-2xl font-headline">Política de Privacidade</DialogTitle>
                        <DialogDescription>
                        Termos de privacidade para {data.siteName || "este site"}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="relative flex-grow min-h-0 overflow-y-auto p-6">
                        <SafeHtmlRenderer htmlString={data.privacyPolicyContent} className="prose prose-sm" />
                    </div>
                    <DialogFooter className="p-6 pt-4 border-t">
                        <DialogClose asChild>
                            <Button type="button" variant="outline">
                                Fechar
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="flex justify-center mt-4">
              <ReportAbuseModal siteData={data} />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Desenvolvido com <Link href="https://www.website.app.br" target="_blank" rel="noopener noreferrer" className="text-template-primary hover:underline">WebSite App</Link>
          </p>
          <p className="mt-1 text-xs text-slate-500">
            O conteúdo deste site (<Link href={currentSiteUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{currentSiteUrl.replace(/^https?:\/\//, '')}</Link>) está sob responsabilidade legal de {data.companyName || data.siteName}.
            Contato: <a href={`mailto:${data.contactEmail}`} className="hover:underline">{data.contactEmail}</a>.
          </p>
        </div>
      </div>
    </footer>
  );
};

interface TemplateRendererProps {
  data: SiteData;
}

export function Template1Renderer({ data }: TemplateRendererProps) {
  const [navLinks, setNavLinks] = useState<{ label: string; href: string }[]>([]);

  useEffect(() => {
    setNavLinks(generateNavigationLinks(data));
  }, [data]);

  const rawCompanyLogoUrl = data.companyLogoUrl;
  let displayLogoAsImage = false;
  let logoSrcForImageComponent = typeof isValidImageUrl === 'function' && isValidImageUrl(rawCompanyLogoUrl) ? rawCompanyLogoUrl! : 'https://placehold.co/150x60.png?text=Logo';
  if (typeof isValidImageUrl === 'function' && isValidImageUrl(rawCompanyLogoUrl)) {
    displayLogoAsImage = true;
  }
  const isPexelsImageForLogo = logoSrcForImageComponent.includes('images.pexels.com');
  
  const sections = React.useMemo(() => [
    { key: 'hero', component: HeroSection, order: data.block2_order, isActive: data.block2_isActive ?? true },
    { key: 'about', component: AboutUsSection, order: data.block3_order, isActive: data.block3_isActive ?? true },
    { key: 'services', component: ServicesSection, order: data.block4_order, isActive: data.block4_isActive ?? true },
    { key: 'differentiators', component: DifferentiatorsSection, order: data.block_diff_order, isActive: data.block_diff_isActive ?? true },
    { key: 'testimonials', component: TestimonialsSection, order: data.block5_order, isActive: data.block5_isActive ?? true },
    { key: 'clientLogos', component: ClientLogosSection, order: data.block6_order, isActive: data.block6_isActive ?? true },
    { key: 'gallery', component: GallerySection, order: data.block7_order, isActive: data.block7_isActive ?? true },
    { key: 'faq', component: FAQSection, order: data.block8_order, isActive: data.block8_isActive ?? true },
    { key: 'locationContact', component: LocationContactSection, order: data.block10_order, isActive: data.block10_isActive ?? true },
    { key: 'cta', component: CTASection, order: data.block9_order, isActive: data.block9_isActive ?? true },
  ], [data]);
  
  const sortedAndActiveSections = React.useMemo(() => sections
    .filter(section => section.isActive)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99)), [sections]);

  const showWhatsappButton = (data.block_footer_showWhatsappButton ?? true) && data.whatsapp;
  const whatsappLink = `https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`;

  return (
    <>
      {data.googleAnalyticsId && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${data.googleAnalyticsId}`}
          />
          <Script
            id="google-analytics-and-ads"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${data.googleAnalyticsId}');
                ${data.googleAdsId ? `gtag('config', '${data.googleAdsId}');` : ''}
              `,
            }}
          />
        </>
      )}
      {data.facebookPixelId && (
        <Script
          id="fb-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${data.facebookPixelId}');
              fbq('track', 'PageView');
            `,
          }}
        />
      )}
      <div className="font-body bg-background text-foreground antialiased">
        <style jsx global>{`
          :root {
            --template-primary-color: ${data.primaryColor || '#29ABE2'};
            --template-secondary-color: ${data.secondaryColor || '#F0F8FF'};
            --template-accent-color: ${data.accentColor || '#90EE90'};
          }
          .text-template-primary { color: var(--template-primary-color); }
          .bg-template-primary { background-color: var(--template-primary-color); }
          .border-template-primary { border-color: var(--template-primary-color); }
          .text-template-secondary { color: var(--template-secondary-color); }
          .bg-template-secondary { background-color: var(--template-secondary-color); }

          .prose {
            --tw-prose-body: inherit;
            --tw-prose-links: var(--template-primary-color);
            --tw-prose-bold: inherit;
            --tw-prose-quote-borders: var(--template-primary-color);
            --tw-prose-invert-body: rgba(255, 255, 255, 0.9);
            --tw-prose-invert-headings: white; 
            --tw-prose-invert-links: white;
            --tw-prose-invert-bold: white;
            --tw-prose-invert-hr: rgba(255, 255, 255, 0.2);
          }
          .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
              font-family: 'Poppins', sans-serif;
          }
          .prose.prose-invert p, .prose.prose-invert ul, .prose.prose-invert ol, .prose.prose-invert blockquote {
                color: rgba(255, 255, 255, 0.9);
          }
          .prose-base h1 { font-size: 1.5rem; line-height: 2rem; margin-top: 1em; margin-bottom: 0.5em; }
          .prose-base h2 { font-size: 1.25rem; line-height: 1.75rem; margin-top: 0.8em; margin-bottom: 0.4em; }

        `}</style>

        <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto flex items-center justify-between px-4">
            <div className="flex-shrink-0">
              {displayLogoAsImage ? (
                <Link href="#hero" className="flex items-center">
                  <Image
                    src={logoSrcForImageComponent}
                    alt={`Logo de ${data.siteName || 'Empresa'}`}
                    width={150}
                    height={100}
                    className="h-auto max-h-[100px] w-auto object-contain"
                    data-ai-hint="company logo"
                    unoptimized={isPexelsImageForLogo}
                  />
                </Link>
              ) : (
                <Link href="#hero" className="font-headline text-2xl font-bold text-template-primary">
                  {(data.siteName && data.siteName.toLowerCase() !== 'string') ? data.siteName : (data.companyName && data.companyName.toLowerCase() !== 'string' ? data.companyName : "Meu Site Incrível")}
                </Link>
              )}
            </div>

            <div className="flex items-center">
              <div className="hidden sm:flex items-center gap-1 md:gap-2">
                {data.contactEmail && (
                  <a href={`mailto:${data.contactEmail}`} aria-label="Email de contato" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={data.contactEmail}>
                    <Mail className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{data.contactEmail}</span>
                  </a>
                )}
                {data.phone && (
                  <a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} aria-label="Telefone de contato" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={`${data.phoneDDI || ''} ${data.phone}`}>
                    <Phone className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{`${data.phoneDDI || ''} ${data.phone}`}</span>
                  </a>
                )}
                {data.whatsapp && (
                  <a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="flex items-center text-gray-600 hover:text-template-primary p-1.5" title={`${data.whatsappDDI || ''} ${data.whatsapp}`}>
                    <MessageCircle className="h-5 w-5" />
                    <span className="ml-1.5 hidden text-sm md:inline">{`${data.whatsappDDI || ''} ${data.whatsapp}`}</span>
                  </a>
                )}
              </div>
              {navLinks.length > 0 && (
                <div className="flex-shrink-0 sm:ml-2">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="icon" className="border-template-primary text-template-primary hover:bg-template-primary/10">
                        <MenuIcon className="h-6 w-6" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[280px] sm:w-[320px] bg-background p-0 text-foreground">
                      <SheetHeader className="border-b p-4">
                        <SheetTitle className="text-template-primary font-headline">Menu</SheetTitle>
                      </SheetHeader>
                      <nav className="flex flex-col space-y-1 p-4">
                        {navLinks.map((link) => (
                          <SheetClose asChild key={link.href}>
                            <Link
                              href={link.href}
                              className="block rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary"
                            >
                              {link.label}
                            </Link>
                          </SheetClose>
                        ))}
                        <div className="border-t pt-4 mt-4 space-y-2 sm:hidden">
                            {data.contactEmail && (
                              <SheetClose asChild>
                                <a href={`mailto:${data.contactEmail}`} className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <Mail className="h-5 w-5 text-template-primary/80"/> Email
                                </a>
                              </SheetClose>
                            )}
                            {data.phone && (
                              <SheetClose asChild>
                                <a href={`tel:${(data.phoneDDI || '').replace(/\D/g, '')}${(data.phone || '').replace(/\D/g, '')}`} className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <Phone className="h-5 w-5 text-template-primary/80"/> Telefone
                                </a>
                              </SheetClose>
                            )}
                            {data.whatsapp && (
                              <SheetClose asChild>
                                <a href={`https://wa.me/${(data.whatsappDDI || '').replace(/\D/g, '')}${(data.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-muted hover:text-template-primary">
                                    <MessageCircle className="h-5 w-5 text-template-primary/80"/> WhatsApp
                                </a>
                              </SheetClose>
                            )}
                        </div>
                      </nav>
                    </SheetContent>
                  </Sheet>
                </div>
              )}
            </div>
          </div>
        </header>

        <main>
          {sortedAndActiveSections.map(({ key, component: Component }) => (
            <Component key={key} data={data} />
          ))}
          <FooterSection data={data} />
        </main>
        
        {showWhatsappButton && (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 flex items-center justify-center"
            aria-label="Fale conosco pelo WhatsApp"
            title="Fale conosco pelo WhatsApp"
          >
            <MessageCircle className="h-8 w-8" />
          </a>
        )}
      </div>
    </>
  );
}
